import { z } from "zod";

/**
 * Every row's ID is a TypeID (https://github.com/jetify-com/typeid, spec 0.3): a per-table prefix,
 * an underscore, and 128 bits in 26 characters of lowercase Crockford base32, as in
 * `conn_01h455vb4pex5vsknk084sn02q`. The prefix says what an ID names wherever it turns up — a log
 * line, a support ticket, a URL — and lets a parser refuse an ID of the wrong kind before it
 * reaches a query.
 *
 * The bits are a UUIDv7, so IDs sort by creation time and new rows land at the end of the
 * primary-key index. A v7 publishes its creation time and has 74 random bits, which is fine for an
 * ID that names a row someone must separately be allowed to see. It is not fine for an ID that is
 * itself the credential: those kinds take 122 random bits and no timestamp.
 *
 * This module is browser-safe: the web app validates IDs with the same schemas the servers use.
 */
export const ID_PREFIXES = {
  // Identity database (Better Auth's tables and ours beside them).
  user: "user",
  session: "sess",
  account: "acct",
  verification: "verif",
  organization: "org",
  workspace: "wksp",
  workspaceMember: "wkspm",
  member: "mem",
  invitation: "inv",
  jwks: "jwk",
  joinRequest: "jreq",
  adminAction: "act",
  deletionRequest: "delreq",
  invitationDelivery: "invd",
  /** A change to someone's membership of a workspace, waiting to reach the workspace's region. */
  memberSync: "msync",
  // Regional database.
  connection: "conn",
  credential: "cred",
  attempt: "att",
  approval: "appr",
  standingApproval: "stap",
  event: "evt",
  thread: "thr",
  message: "msg",
  run: "run",
  humanRequest: "hreq",
  artifact: "art",
  inboxItem: "inb",
  modelCall: "mcl",
  file: "file",
  fileRef: "fref",
  process: "proc",
  processDraft: "pdraft",
  /** A step's stable ID: the same in every draft and release of its process. */
  processStep: "step",
  processChange: "pchg",
  processQuestion: "pqst",
  processRelease: "prel",
  /** A draft frozen at one revision for a try to run (decision `2026-11-03-tries-on-the-case-runner-build-plan`). */
  processSnapshot: "psnap",
  /** A step or fact that a draft and the live version both changed, waiting for a person. */
  processConflict: "pcnf",
  /** What one Undo takes back whole: one person's edit in one step, or one reply (ENG-1065). */
  playbookChangeGroup: "pbgrp",
  /** A table of the firm's facts and lookups (decision 116). */
  dataTable: "dtbl",
  /** A table's column: stable across renames, so steps and releases name it by ID. */
  dataColumn: "dcol",
  dataTableRow: "drow",
  /** A recording on a chat: its interview session's tape is stored with it. */
  recording: "rec",
  /** A still of the screen kept during a recording, and the file that holds it (ENG-1024). */
  recordingScreen: "rscr",
  /**
   * Lets the desktop app add screens to one recording. Its ID only finds the row; the credential is
   * the secret sent beside it, stored hashed.
   */
  captureGrant: "capg",
  /** One line of a recording's light action trace: an app, page, field, click, copy or download (ENG-1027). */
  recordingTrace: "rtrc",
  /** A recording's speech usage for one job of its voice worker and one model (decision `2026-10-30-voice-worker-speech-usage-ledger`). */
  speechUsage: "spu",
  /** One run of a process on one release, about one subject (decision 118). */
  case: "case",
  /** One step of a case for one Each item: its frozen inputs and its thread. */
  caseStep: "cstep",
  /** What a step of a case produced, under a key its step declares (decision 122). */
  caseOutput: "cout",
  /** A verdict on a step's condition for one body of evidence. */
  caseJudgment: "cjdg",
  /** A case waiting at a step on a question (decision 124). */
  caseHold: "chold",
  /** What a step of a case changes or sends in another system (decision 126). */
  caseEffect: "ceff",
  /**
   * A teammate: a named owner of one part of a workspace's work and its playbooks, with no person
   * of its own (decision `2026-11-01-a-teammate-owns-playbooks-and-has-no-person`).
   */
  teammate: "tmate",
} as const;

export type IdKind = keyof typeof ID_PREFIXES;

/**
 * Kinds whose ID is the only credential a link carries — the invitation accept link — so it must
 * stay unguessable and must not date itself. Everything else is time-ordered.
 */
const OPAQUE_KINDS: ReadonlySet<IdKind> = new Set(["invitation"]);

const ALPHABET = "0123456789abcdefghjkmnpqrstvwxyz";
const DECODE = new Map(ALPHABET.split("").map((character, index) => [character, index]));
const PREFIX = /^([a-z]([a-z_]{0,61}[a-z])?)?$/;
const SUFFIX_LENGTH = 26;

/** A byte that is always in range here; the fallback only satisfies the index type. */
const byteAt = (bytes: Uint8Array, index: number) => bytes[index] ?? 0;

/** Marks 16 bytes as an RFC 9562 UUID of `version`: its version nibble and the RFC variant. */
function markVersion<Bytes extends Uint8Array>(bytes: Bytes, version: number): Bytes {
  bytes[6] = (byteAt(bytes, 6) & 0x0f) | (version << 4);
  bytes[8] = (byteAt(bytes, 8) & 0x3f) | 0x80;
  return bytes;
}

/** 16 bytes as 26 base32 characters: two zero pad bits, then the 128 bits most significant first. */
function encodeSuffix(bytes: Uint8Array): string {
  let out = "";
  for (let index = 0; index < SUFFIX_LENGTH; index++) {
    let value = 0;
    for (let bit = index * 5 - 2; bit < index * 5 + 3; bit++) {
      const set = bit >= 0 && (byteAt(bytes, bit >> 3) >> (7 - (bit & 7))) & 1;
      value = (value << 1) | (set ? 1 : 0);
    }
    out += ALPHABET[value];
  }
  return out;
}

function decodeSuffix(suffix: string): Uint8Array | null {
  if (suffix.length !== SUFFIX_LENGTH) return null;
  const bytes = new Uint8Array(16);
  for (let index = 0; index < SUFFIX_LENGTH; index++) {
    const value = DECODE.get(suffix.charAt(index));
    if (value === undefined) return null;
    for (let offset = 0; offset < 5; offset++) {
      const bit = index * 5 - 2 + offset;
      if (!((value >> (4 - offset)) & 1)) continue;
      // A set pad bit means more than 128 bits: the first character must be 0–7.
      if (bit < 0) return null;
      bytes[bit >> 3] = byteAt(bytes, bit >> 3) | (1 << (7 - (bit & 7)));
    }
  }
  return bytes;
}

export function encodeTypeId(prefix: string, bytes: Uint8Array): string {
  if (!PREFIX.test(prefix)) throw new Error(`Invalid TypeID prefix: ${prefix}`);
  if (bytes.length !== 16) throw new Error("A TypeID holds exactly 16 bytes.");
  const suffix = encodeSuffix(bytes);
  return prefix ? `${prefix}_${suffix}` : suffix;
}

/** The prefix and 16 bytes of a well-formed TypeID, or null for anything else. */
export function decodeTypeId(id: string): { prefix: string; bytes: Uint8Array } | null {
  const separator = id.lastIndexOf("_");
  const prefix = separator === -1 ? "" : id.slice(0, separator);
  // An empty prefix is written without the separator, so "_…" is malformed.
  if (separator === 0 || !PREFIX.test(prefix)) return null;
  const bytes = decodeSuffix(id.slice(separator + 1));
  return bytes ? { prefix, bytes } : null;
}

/** A UUID's 16 bytes, accepting either case, or null if it is not a UUID. */
export function uuidBytes(uuid: string): Uint8Array | null {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid)) return null;
  const hex = uuid.replaceAll("-", "");
  return Uint8Array.from({ length: 16 }, (_, index) =>
    Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16),
  );
}

export function bytesToUuid(bytes: Uint8Array): string {
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Where minting takes the time and fills random bytes; tests pass fixed ones. */
type IdSource = { now?: () => number; random?: (bytes: Uint8Array<ArrayBuffer>) => void };

function randomBytes(source: IdSource): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(16);
  if (source.random) source.random(bytes);
  else crypto.getRandomValues(bytes);
  return bytes;
}

/** RFC 9562 UUIDv7: 48 bits of Unix milliseconds, version 7, the RFC variant, 74 random bits. */
export function uuidV7Bytes(source: IdSource = {}): Uint8Array {
  const bytes = randomBytes(source);
  let time = (source.now ?? Date.now)();
  for (let index = 5; index >= 0; index--) {
    bytes[index] = time % 256;
    time = Math.floor(time / 256);
  }
  return markVersion(bytes, 7);
}

/** RFC 9562 UUIDv4: 122 random bits and nothing else. */
function uuidV4Bytes(source: IdSource = {}): Uint8Array {
  return markVersion(randomBytes(source), 4);
}

/** A fresh ID of `kind`. The column defaults, Better Auth and the stores all mint through this. */
export function newId(kind: IdKind, source?: IdSource): string {
  const bytes = OPAQUE_KINDS.has(kind) ? uuidV4Bytes(source) : uuidV7Bytes(source);
  return encodeTypeId(ID_PREFIXES[kind], bytes);
}

/**
 * The ID an existing row was given when the databases moved to TypeIDs — the same mapping the
 * 18 October 2026 migrations apply in SQL, so an old ID found in a log or an email can be looked up.
 * A UUID keeps its 128 bits; any other legacy ID takes the first 16 bytes of its SHA-256, marked as
 * a version 8 UUID; an ID that is already of this kind is returned unchanged.
 */
export async function legacyToTypeId(kind: IdKind, legacy: string): Promise<string> {
  const prefix = ID_PREFIXES[kind];
  if (isId(kind, legacy)) return legacy;
  let bytes = uuidBytes(legacy);
  if (!bytes) {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(legacy));
    bytes = markVersion(new Uint8Array(digest).slice(0, 16), 8);
  }
  return encodeTypeId(prefix, bytes);
}

/** Whether `value` is a well-formed ID of `kind`. */
export function isId(kind: IdKind, value: string): boolean {
  return decodeTypeId(value)?.prefix === ID_PREFIXES[kind];
}

/** Validates an ID of one kind. An ID of another kind is as invalid as a malformed one. */
export function idSchema(kind: IdKind) {
  return z.string().refine((value) => isId(kind, value), { error: `Expected a ${kind} ID.` });
}
